// src/utils/firebase/collection.js
import { 
  doc, 
  getDoc, 
  collection, 
  writeBatch, 
  getDocs, 
  increment,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase.utils';
import _ from 'lodash';
import { clearCardsFromQueue, setCardData, invalidateCollection } from '../../store/card/card.action';
import { setSystemNotification } from '../../store/system/system.action';
import { logUserAction } from './activity';
import { serializeFirestoreData, sanitizeForFirestore } from './serialization';
import { computeAllCardFields } from './computedFields';
import { syncPublicProfileInBatch } from './publicProfile';
import { normalizeForCompare } from '../textNormalization';

/**
 * Merges queued cards with Firestore and updates Redux state
 * @param {string} userID - User ID
 * @param {Object} queuedCards - Queued cards data
 * @param {number} prevCount - Previous card count
 * @returns {Function} Redux thunk function
 */
export const mergeQueuedCardsWithFirestore = (userID, queuedCards, prevCount) => {
  return async (dispatch) => {
    try {
      await addCardsToFirestore(userID, queuedCards);
      const addedCount = queuedCards?.totalCardCount;
      const increment = typeof addedCount === 'number' && !Number.isNaN(addedCount) ? addedCount : 0;
      logUserAction(userID, "addedToCollection", {
        cards: queuedCards,
        prevCount: prevCount ?? 0,
        increment
      });
      dispatch(setSystemNotification(`Successfully added ${queuedCards.totalCardCount} ${queuedCards.totalCardCount === 1 ? "card" : "cards"}!`));
      dispatch(clearCardsFromQueue("removed"));
      dispatch(invalidateCollection());
    } catch (error) {
      dispatch(setSystemNotification('Error adding cards to collection. Please try again.'));
    }
  };
};

/**
 * Adds cards to Firestore collection
 * @param {string} userID - User ID
 * @param {Object} queuedCards - Queued cards data
 */
export const addCardsToFirestore = async (userID, queuedCards) => {
  const batch = writeBatch(db);

  let newCollectedYears = {};
  let newCollectedSeries = {};
  let newCollectedCardTypeCategories = {};

  let totalNewCardCount = 0;
  let manufacturerNewCardsCount = 0;
  let seriesNewCardsCount = 0;
  let cardTypeCategoryNewCardsCount = 0;

  const calculateTotalCardCount = (object) => {
    return Object.values(object).reduce((total, item) => total + (item.cardCount || 0), 0);
  };

  // Helper function to sanitize collectedCards array - Firestore does not allow undefined
  const sanitizeCollectedCards = (collectedCards) => {
    return (collectedCards || []).map((card) => ({
      cardParallelName: card.cardParallelName ?? null,
      cardColor: card.cardColor ?? null,
      cardQty: card.cardQty ?? 0,
      cardPrintNumber: card.cardPrintNumber ?? null,
      cardSupply: card.cardSupply ?? null,
      cardOdds: card.cardOdds ?? null
    }));
  };

  for (const sport of Object.keys(queuedCards)) {
    if (sport === "totalCardCount") {
      totalNewCardCount = queuedCards[sport];
      continue;
    }
    const sportData = queuedCards[sport];

    // fetch collectedYears for each Sport
    const sportDocRef = doc(db, `users/${userID}/collection/${sport}`);
    try {
      const sportDocSnap = await getDoc(sportDocRef);
      if (sportDocSnap.exists()) {
        const data = sportDocSnap.data();
        newCollectedYears = _.cloneDeep(data.collectedYears) || {};
      }
    } catch (error) {
      // Error fetching collectedYears - continue with empty
    }

    for (const year of Object.keys(sportData)) {
      const yearData = sportData[year];

      for (const manufacturer of Object.keys(yearData)) {
        const manufacturerData = yearData[manufacturer];

        // fetch collectedSeries for each manufacturer
        const manufacturerDocRef = doc(db, `users/${userID}/collection/${sport}/${year}/${manufacturer}`);
        try {
          const manufacturerDocSnap = await getDoc(manufacturerDocRef);
          if (manufacturerDocSnap.exists()) {
            const data = manufacturerDocSnap.data();
            newCollectedSeries = _.cloneDeep(data.collectedSeries) || {};
          }
        } catch (error) {
          // Error fetching collectedSeries - continue with empty
        }

        for (const series of Object.keys(manufacturerData)) {
          if (series === "totalCardCount") {
            manufacturerNewCardsCount = manufacturerData[series];
            continue;
          }
          const seriesData = manufacturerData[series];

          for (const cardType of Object.keys(seriesData)) {
            if (cardType === "totalCardCount") {
              seriesNewCardsCount = seriesData[cardType];
              continue;
            }
            const cardTypeData = seriesData[cardType];

            // fetch collectedCardTypeCategories
            const cardTypeDocRef = doc(db, `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}`);
            try {
              const cardTypeDocSnap = await getDoc(cardTypeDocRef);
              if (cardTypeDocSnap.exists()) {
                const data = cardTypeDocSnap.data();
                newCollectedCardTypeCategories = _.cloneDeep(data.collectedCardTypeCategories) || {};
              }
            } catch (error) {
              // Error fetching collectedCardTypeCategories - continue with empty
            }

            for (const cardTypeCategory of Object.keys(cardTypeData)) {
              if (cardTypeCategory === "totalCardCount") {
                continue;
              }
              const cardTypeCategoryData = cardTypeData[cardTypeCategory];
              
              // Check if this is a custom category
              const isCustomCategory = cardTypeCategory.startsWith('custom:');
              const customCategoryId = isCustomCategory ? cardTypeCategory.replace('custom:', '') : null;
              
              // Track new unique cards for custom category cardCount
              let newUniqueCardsCount = 0;

              for (const cardNumber of Object.keys(cardTypeCategoryData)) {
                if (cardNumber === "totalCardCount") {
                  cardTypeCategoryNewCardsCount = cardTypeCategoryData[cardNumber];
                  continue;
                }
                const cardData = cardTypeCategoryData[cardNumber];
                // For custom categories, use the full cardTypeCategory (with "custom:" prefix) for the Firestore path
                // This matches the actual Firestore structure where the collection name includes the full prefix
                const categoryPathSegment = cardTypeCategory;
                const cardDocRef = doc(db, `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/${categoryPathSegment}/${cardNumber}`);

                // Fetch existing card to determine if it's new or duplicate
                const existingCardSnap = await getDoc(cardDocRef);
                const isNewCard = !existingCardSnap.exists();
                let existingCollectedCards = [];
                
                // Track if this is a new card for custom category counting
                if (isCustomCategory && isNewCard) {
                  newUniqueCardsCount++;
                }

                // If the card exists, check the variations
                if (existingCardSnap.exists()) {
                  const existingCardData = existingCardSnap.data();
                  existingCollectedCards = existingCardData.collectedCards || [];

                  // Check each variation in cardData.collectedCards
                  // Normalize undefined to null for comparison (existing data from Firestore might have undefined)
                  cardData.collectedCards.forEach((parallel) => {
                    const normalizedParallel = {
                      cardParallelName: parallel.cardParallelName,
                      cardPrintNumber: parallel.cardPrintNumber ?? null,
                      cardSupply: parallel.cardSupply ?? null
                    };
                    
                    const existingVariation = existingCollectedCards.find(
                      (p) => {
                        // Normalize existing variation fields (might be undefined from old Firestore data)
                        const normalizedExisting = {
                          cardParallelName: p.cardParallelName,
                          cardPrintNumber: p.cardPrintNumber ?? null,
                          cardSupply: p.cardSupply ?? null
                        };
                        
                        // Match all three fields: name (case-insensitive), printNumber, and supply
                        return (
                          normalizeForCompare(normalizedExisting.cardParallelName) === normalizeForCompare(normalizedParallel.cardParallelName) &&
                          normalizedExisting.cardPrintNumber === normalizedParallel.cardPrintNumber &&
                          normalizedExisting.cardSupply === normalizedParallel.cardSupply
                        );
                      }
                    );

                    if (existingVariation) {
                      // Increment the quantity for this variation
                      existingVariation.cardQty += parallel.cardQty;
                    } else {
                      // Add a new variation if it doesn't exist (no undefined - Firestore invalid)
                      const newVariation = {
                        cardParallelName: parallel.cardParallelName ?? null,
                        cardColor: parallel.cardColor ?? null,
                        cardQty: parallel.cardQty ?? 0,
                        cardPrintNumber: parallel.cardPrintNumber ?? null,
                        cardSupply: parallel.cardSupply ?? null,
                        cardOdds: parallel.cardOdds ?? null
                      };
                      existingCollectedCards.push(newVariation);
                    }
                  });
                } else {
                  // If the card doesn't exist, initialize collectedCards from the queued data
                  existingCollectedCards = cardData.collectedCards;
                }

                // Sanitize collectedCards to normalize undefined to null for cardPrintNumber and cardSupply
                const sanitizedCollectedCards = sanitizeCollectedCards(existingCollectedCards);

                // Set or merge card data in Firestore with updated variations (no undefined - Firestore invalid)
                const cardUpdateData = {
                  cardNumber: cardData.cardNumber ?? null,
                  name: cardData.name || [],
                  team: cardData.team || [],
                  cardAttributes: cardData.cardAttributes || [],
                  collectedCards: sanitizedCollectedCards,
                  quantity: increment(cardData.quantity)
                };
                
                // Only include checklistPath for official categories, not custom ones
                if (!isCustomCategory && cardData.checklistPath) {
                  cardUpdateData.checklistPath = cardData.checklistPath;
                }

                // Compute optimized fields for filtering performance
                const computedFields = computeAllCardFields({
                  ...cardData,
                  collectedCards: sanitizedCollectedCards
                });
                Object.assign(cardUpdateData, sanitizeForFirestore(computedFields));

                batch.set(cardDocRef, cardUpdateData, { merge: true });
              }

              if (newCollectedCardTypeCategories[cardTypeCategory]) {
                // If the year already exists, add the existing cardCount to manufacturerNewCardsCount
                newCollectedCardTypeCategories[cardTypeCategory].cardCount += cardTypeCategoryNewCardsCount;
              } else {
                // If the year does not exist, initialize it with manufacturerNewCardsCount
                newCollectedCardTypeCategories[cardTypeCategory] = {
                  cardCount: cardTypeCategoryNewCardsCount,
                  title: cardTypeCategory,
                };
              }

              // Update custom category document's cardCount if this is a custom category
              // Only count new unique cards, not variations of existing cards
              if (isCustomCategory && customCategoryId && newUniqueCardsCount > 0) {
                const customCategoryPath = `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/customCategories/${customCategoryId}`;
                const customCategoryDocRef = doc(db, customCategoryPath);
                const customCategorySnap = await getDoc(customCategoryDocRef);
                const currentCardCount = customCategorySnap.exists() ? (customCategorySnap.data().cardCount || 0) : 0;
                
                batch.set(customCategoryDocRef, {
                  cardCount: currentCardCount + newUniqueCardsCount
                }, { merge: true });
              }
            }

            const newTotalCount = calculateTotalCardCount(newCollectedCardTypeCategories);
            batch.set(cardTypeDocRef, {
              collectedCardTypeCategories: newCollectedCardTypeCategories,
              totalCardCount: newTotalCount,
            }, { merge: true });
            newCollectedCardTypeCategories = {}
          }

          if (newCollectedSeries[series]) {
            // If the year already exists, add the existing cardCount to manufacturerNewCardsCount
            newCollectedSeries[series].cardCount += seriesNewCardsCount;
          } else {
            // If the year does not exist, initialize it with manufacturerNewCardsCount
            newCollectedSeries[series] = {
              cardCount: seriesNewCardsCount,
              title: series,
            };
          }
        }
        const newTotalManufactuereCardCount = calculateTotalCardCount(newCollectedSeries);
        batch.set(manufacturerDocRef, {
          collectedSeries: newCollectedSeries,
          totalCardCount: newTotalManufactuereCardCount,
        }, { merge: true });
      }

      if (newCollectedYears[year]) {
        newCollectedYears[year].cardCount += manufacturerNewCardsCount;
      } else {
        newCollectedYears[year] = {
          cardCount: manufacturerNewCardsCount,
          title: year,
        };
      }
    }
    const newTotalCardCount = calculateTotalCardCount(newCollectedYears);
    batch.set(sportDocRef, {
      collectedYears: newCollectedYears,
      totalCardCount: newTotalCardCount,
    }, { merge: true });
  }

  // Update user's total collection counts (in user document)
  const userDocRef = doc(db, `users/${userID}`);
  const userSnap = await getDoc(userDocRef);
  const existingUserData = userSnap.exists() ? userSnap.data() : {};

  // Accumulate the current total counts with the new values
  const updatedCollectionCardCount = (existingUserData.collectionCardCount || 0) + totalNewCardCount;

  const userCountUpdate = {
    collectionCardCount: updatedCollectionCardCount,
    collectionLastUpdated: Timestamp.now()
  };

  batch.set(userDocRef, userCountUpdate, { merge: true });
  syncPublicProfileInBatch(batch, userID, userCountUpdate);

  // Commit batch operations
  await batch.commit();
};

/**
 * Removes a card from Firestore collection
 * @param {string} userID - User ID
 * @param {string} parallelCardName - Parallel card name
 * @param {number} inc - Increment amount
 * @param {string} sport - Sport
 * @param {string} year - Year
 * @param {string} manufacturer - Manufacturer
 * @param {string} series - Series
 * @param {string} cardType - Card type
 * @param {string} cardTypeCategory - Card type category
 * @param {string} cardNumber - Card number
 * @param {number} prevCount - Previous count
 * @param {Object} card - Card data
 * @param {string|Object} parallelCardName - Parallel name string, or variation object { cardParallelName, cardPrintNumber?, cardSupply? } for exact match (e.g. custom serial)
 * @param {boolean} [skipLog=false] - When true, skip the per-card activity entry
 *   so a caller can write one batched log instead (e.g. marking a sale sold).
 */
export const removeCardFromFirestore = async (
  userID,
  parallelCardName,
  inc,
  sport,
  year,
  manufacturer,
  series,
  cardType,
  cardTypeCategory,
  cardNumber,
  prevCount,
  card,
  skipLog = false
) => {
  // For custom categories, use the full cardTypeCategory (with "custom:" prefix) for the Firestore path
  // This matches the actual Firestore structure where the collection name includes the full prefix
  const categoryPathSegment = cardTypeCategory;
  
  // e.g. "1"
  const cardDocRef = doc(db, `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}/${categoryPathSegment}/${cardNumber}`);

  try {
    const cardSnap = await getDoc(cardDocRef);
    if (!cardSnap.exists()) throw new Error("Card not found!");

    const cardData = cardSnap.data();
    const { collectedCards } = cardData;
    const matchVariation = (v) => {
      const name = typeof parallelCardName === 'object' && parallelCardName !== null
        ? parallelCardName.cardParallelName
        : parallelCardName;
      if (normalizeForCompare(v.cardParallelName) !== normalizeForCompare(name)) return false;
      if (typeof parallelCardName === 'object' && parallelCardName !== null) {
        if (parallelCardName.cardPrintNumber != null && v.cardPrintNumber !== parallelCardName.cardPrintNumber) return false;
        if (parallelCardName.cardSupply != null && v.cardSupply !== parallelCardName.cardSupply) return false;
      }
      return true;
    };
    const parallelIndex = collectedCards.findIndex(matchVariation);

    if (parallelIndex === -1) throw new Error(`Parallel not found!`);

    // Save the removed card data before modifying the array
    const removedCards = { ...collectedCards[parallelIndex] };
    const originalQty = removedCards.cardQty;

    const newQuantity = collectedCards[parallelIndex].cardQty - inc;
    const removeAll = (newQuantity <= 0);

    if (removeAll) {
      collectedCards.splice(parallelIndex, 1);
    } else {
      collectedCards[parallelIndex].cardQty = newQuantity;
    }

    const newTotalQuantity = collectedCards.reduce((sum, parallel) => sum + parallel.cardQty, 0);

    const batch = writeBatch(db);

    const handleUpdate = (data, field, incValue) => {
      if (data[field]) {
        data[field].cardCount -= incValue;
        if (removeAll) data[field].uniqueCardCount -= 1;
        if (data[field].cardCount <= 0) delete data[field];
      }
    };

    // last collectedCard parallel was removed, so delete the cardDocRef (e.g. "1")
    if (newTotalQuantity <= 0) {
      batch.delete(cardDocRef);
    } else {
      // Compute optimized fields for filtering performance
      const updatedCardData = {
        ...cardData,
        collectedCards: collectedCards,
        quantity: newTotalQuantity
      };
      const computedFields = computeAllCardFields(updatedCardData);
      
      batch.update(cardDocRef, {
        collectedCards: collectedCards,
        quantity: newTotalQuantity,
        ...computedFields
      });
    }

    // update collectedCardTypeCategories values
    const cardTypeDocRef = doc(db, `users/${userID}/collection/${sport}/${year}/${manufacturer}/${series}/${cardType}`);
    const cardTypeSnap = await getDoc(cardTypeDocRef);

    if (cardTypeSnap.exists()) {
      const cardTypeData = cardTypeSnap.data();
      const updatedCollectedCardTypeCategories = { ...cardTypeData.collectedCardTypeCategories };

      handleUpdate(updatedCollectedCardTypeCategories, cardTypeCategory, inc);

      const newCardTypeCount = Object.values(updatedCollectedCardTypeCategories).reduce((sum, category) => sum + category.cardCount, 0);

      if (newCardTypeCount <= 0) {
        batch.delete(cardTypeDocRef);
      } else {
        batch.update(cardTypeDocRef, {
          totalCardCount: newCardTypeCount,
          collectedCardTypeCategories: updatedCollectedCardTypeCategories
        });
      }
    }

    // update collectedSeries values
    const manufacturerDocRef = doc(db, `users/${userID}/collection/${sport}/${year}/${manufacturer}`);
    const manufacturerSnap = await getDoc(manufacturerDocRef);

    if (manufacturerSnap.exists()) {
      const manufacturerData = manufacturerSnap.data();
      const updatedCollectedSeries = { ...manufacturerData.collectedSeries };

      handleUpdate(updatedCollectedSeries, series, inc);

      const newSeriesCardCount = Object.values(updatedCollectedSeries).reduce((sum, series) => sum + series.cardCount, 0);

      if (newSeriesCardCount <= 0) {
        batch.delete(manufacturerDocRef);
      } else {
        batch.update(manufacturerDocRef, {
          totalCardCount: newSeriesCardCount,
          collectedSeries: updatedCollectedSeries
        });
      }
    }

    // update collectedYears values
    const yearDocRef = doc(db, `users/${userID}/collection/${sport}`);
    const yearSnap = await getDoc(yearDocRef);

    if (yearSnap.exists()) {
      const yearData = yearSnap.data();
      const updatedCollectedYears = { ...yearData.collectedYears };

      handleUpdate(updatedCollectedYears, year, inc);

      const newYearCardCount = Object.values(updatedCollectedYears).reduce((sum, year) => sum + year.cardCount, 0);

      if (newYearCardCount <= 0) {
        batch.delete(yearDocRef);
      } else {
        batch.update(yearDocRef, {
          totalCardCount: newYearCardCount,
          collectedYears: updatedCollectedYears
        });
      }
    }

    // update user top-level values
    const userDocRef = doc(db, `users/${userID}`);
    const userSnap = await getDoc(userDocRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();

      let newUserCardCount = userData.collectionCardCount - inc;
      let newUserUniqueCardCount = removeAll ? userData.uniqueCardCount - 1 : userData.uniqueCardCount

      newUserCardCount = Math.max(0, newUserCardCount);
      newUserUniqueCardCount = Math.max(0, newUserUniqueCardCount);

      const userCountUpdate = {
        collectionCardCount: newUserCardCount,
        uniqueCardCount: newUserUniqueCardCount,
        collectionLastUpdated: Timestamp.now()
      };

      batch.update(userDocRef, userCountUpdate);
      syncPublicProfileInBatch(batch, userID, userCountUpdate);
    }

    await batch.commit();

    const increment = removeAll ? originalQty * -1 : -1;

    removedCards.cardQty = inc * -1;

    const cardObject = {
      [sport]: {
        [year]: {
          [manufacturer]: {
            [series]: {
              [cardType]: {
                [cardTypeCategory]: {
                  [cardNumber]: {
                    ...card,
                    collectedCards: [
                      removedCards
                    ]
                  }
                }
              }
            }
          }
        }
      }
    }

    if (!skipLog) {
      // Don't await logUserAction - it's fire-and-forget and shouldn't block the operation
      logUserAction(userID, "removedFromCollection", { cards: cardObject, prevCount: prevCount, increment });
    }

    return { success: true };

  } catch (error) {
    throw error; // Re-throw so caller can handle it
  }
};

/**
 * Fetches collection data from Firestore without dispatching to Redux
 * @param {string} collectionPath - Collection path
 * @returns {Promise<Object>} Collection data object
 */
export const fetchCollectionDataOnly = async (collectionPath) => {
  try {
    const collectionRef = collection(db, collectionPath);
    const collectionSnapshot = await getDocs(collectionRef);
    const collectionData = {};

    collectionSnapshot.forEach((doc) => {
      // Serialize Firestore data to handle Timestamps and other non-serializable values
      const data = serializeFirestoreData(doc.data());
      collectionData[doc.id] = data;
    });

    return collectionData;
  } catch (error) {
    return {};
  }
};

/**
 * Fetches collection data and dispatches to Redux
 * @param {string} collectionPath - Collection path
 * @returns {Function} Redux thunk function
 */
export const fetchCollectionData = (collectionPath) => async (dispatch) => {
  try {
    const collectionData = await fetchCollectionDataOnly(collectionPath);
    dispatch(setCardData(collectionData));
    return collectionData; // Return data for use in viewingCardData
  } catch (error) {
    dispatch(setSystemNotification('Error fetching collection data'));
    return {};
  }
};
